// src/pages/B2B/event-reports/components/SpotlightVenueSelect.tsx
import React, { useEffect, useMemo, useState } from 'react';
import type { Env } from '@/pages/Admin/shared';
import { useAdminApi } from '@/pages/Admin/shared/adminApi';

interface Props {
  env: Env;
  timezone: string;
  mode: 'auto' | 'manual' | 'none';
  businessId: string | null;
  onChange: (next: { mode: 'auto' | 'manual' | 'none'; businessId: string | null }) => void;
}

interface VenueOption {
  id: string;
  name: string;
  timezone: string | null;
}

export function SpotlightVenueSelect({ env, timezone, mode, businessId, onChange }: Props): React.ReactElement {
  const { request } = useAdminApi();
  const [businesses, setBusinesses] = useState<VenueOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    request<{ items?: VenueOption[] }>({ env, path: '/admin/businesses', query: { limit: 1000 } })
      .then((r) => { if (!cancelled) setBusinesses(r.items ?? []); })
      .catch(() => { if (!cancelled) setBusinesses([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [request, env]);

  // The report only covers venues in its timezone, so only those can be picked.
  const tzMatched = useMemo(
    () =>
      businesses
        .filter((b) => b.timezone === timezone)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [businesses, timezone],
  );

  const selectedValue =
    mode === 'auto' ? '__auto__' : mode === 'none' ? '__none__' : (businessId ?? '__auto__');

  return (
    <select
      value={selectedValue}
      onChange={(e) => {
        const v = e.target.value;
        if (v === '__auto__') onChange({ mode: 'auto', businessId: null });
        else if (v === '__none__') onChange({ mode: 'none', businessId: null });
        else onChange({ mode: 'manual', businessId: v });
      }}
      disabled={loading}
      className="px-3 py-2 text-sm bg-gray-900 border border-gray-700 rounded-lg text-gray-200 focus:outline-none focus:border-violet-500"
    >
      <option value="__auto__">Auto-pick top performer</option>
      <option value="__none__">None (leave the spotlight out)</option>
      <optgroup label="Pick a business">
        {tzMatched.map((b) => (
          <option key={b.id} value={b.id}>{b.name}</option>
        ))}
      </optgroup>
    </select>
  );
}
